/**
 * Action category DATA — the static priority buckets, labels, and id→category /
 * id→order maps that drive row/context-menu action ordering + grouping.
 *
 * Split out of `action-categories.ts` (organization only — no behavior change).
 * The pure grouping/sort/partition LOGIC lives in `./grouping.ts`; the original
 * `action-categories` module re-exports both.
 *
 * See .claude/rules/ENTITY_TABLE_ROW_ACTIONS.md (the surfaces this drives).
 */

import type { ActionId } from "../types/action-names";

/** Priority buckets, in display order (index = rank). */
export const CATEGORY_ORDER = [
  "primary", // headline next-step: status, edit, complete, approve, sync
  "communicate", // email, call, text, log activity, comment, meeting
  "create", // task, ticket, deal, time, contract, duplicate
  "status", // state transitions: won/lost, followed-up, snooze, assign, paid
  "navigate", // view, view-company, link, open Slack channel, copy link
  "integration", // enrich, automation, send-to-slack, research, attribution
  "admin", // grant access, consolidate, move-to-internal, reassign, export
  "destructive", // delete, terminate, withdraw, reject
] as const;

export type ActionCategory = (typeof CATEGORY_ORDER)[number];

/** Human label rendered as the section header for each group. */
export const CATEGORY_LABEL: Record<ActionCategory, string> = {
  primary: "Actions",
  communicate: "Communicate",
  create: "Create",
  status: "Update status",
  navigate: "Open & link",
  integration: "Integrations",
  admin: "Admin",
  destructive: "Danger zone",
};

/** category → rank (index in CATEGORY_ORDER) for stable priority sorting. */
export const RANK: Record<ActionCategory, number> = CATEGORY_ORDER.reduce(
  (acc, cat, i) => {
    acc[cat] = i;
    return acc;
  },
  {} as Record<ActionCategory, number>,
);

/**
 * id → category. Covers every member of the ActionId union (+ the handful of
 * inline custom ids used in feature action files). Unknown ids fall back to
 * `create` via getActionCategory.
 */
export const ACTION_CATEGORY_BY_ID: Partial<Record<ActionId, ActionCategory>> = {
  // --- primary (headline next step) ---
  "ask-meridian": "primary",
  "status-dropdown": "primary",
  edit: "primary",
  "quick-complete": "primary",
  "add-update": "primary",
  "quick-approve": "primary",
  "sync-quickbooks": "primary",

  // --- communicate ---
  "send-email": "communicate",
  call: "communicate",
  "call-contact": "communicate",
  "send-text-message": "communicate",
  "send-text-template": "communicate",
  "schedule-meeting": "communicate",
  "log-activity": "communicate",
  "add-comment": "communicate",
  "add-task-note": "communicate",
  "send-ticket-ack-email": "communicate",
  "send-ticket-resolved-email": "communicate",
  "send-ticket-quick-reply": "communicate",

  // --- create ---
  "create-tasks": "create",
  "make-ticket": "create",
  "log-time-entry": "create",
  "log-deal": "create",
  "generate-contract": "create",
  "create-sm-agreement": "create",
  "create-grant-agreement": "create",
  "fill-checklists": "create",
  "fill-both-checklists": "create",
  "fill-client-checklist": "create",
  "fill-telecom-checklist": "create",
  duplicate: "create",
  "duplicate-task": "create",
  "request-quote": "create",
  "convert-to-deal": "create",
  convertToDeal: "create",
  "download-pdf": "create",
  downloadOfferingPdf: "create",
  "charge-card-on-file": "create",
  chargeCardOnFile: "create",

  // --- status (state transitions) ---
  "complete-task": "status",
  "close-ticket": "status",
  "mark-won": "status",
  markWon: "status",
  "mark-lost": "status",
  markLost: "status",
  "mark-followed-up": "status",
  markFollowedUpThisWeek: "status",
  "unmark-followed-up": "status",
  unmarkFollowedUpThisWeek: "status",
  "snooze-deal": "status",
  snoozeDeal: "status",
  "make-project-channel": "communicate",
  "unsnooze-deal": "status",
  unsnoozeDeal: "status",
  "set-followup-date": "status",
  setFollowUpDate: "status",
  "clear-followup-date": "status",
  clearFollowUpDate: "status",
  "mark-paid": "status",
  markDealPaid: "status",
  "mark-in-progress": "status",
  markDealInProgress: "status",
  approve: "status",
  reject: "status",
  "assign-ticket": "status",
  "assign-task-to-me": "status",
  "assign-ticket-to-me": "status",
  "assign-deal-to-me": "status",
  renew: "status",
  resend: "status",
  "change-schedule": "status",
  changeSchedule: "status",
  "set-due-date": "status",

  // --- navigate (open & link) ---
  "view-details": "navigate",
  "view-document": "navigate",
  "view-company": "navigate",
  "view-ticket": "navigate",
  "view-support-inbox": "navigate",
  "view-contact": "navigate",
  viewDeal: "navigate",
  viewEmployee: "navigate",
  viewOffering: "navigate",
  viewCustomer: "navigate",
  "link-company": "navigate",
  "link-contact": "navigate",
  "make-contact": "navigate",
  "open-slack-channel": "navigate",
  "copy-link": "navigate",
  "save-meeting-to-drive": "navigate",
  "report-issue": "navigate",

  // --- integration ---
  enrich: "integration",
  "enrich-contact": "integration",
  "enrich-company": "integration",
  "configure-automated-tasks": "integration",
  "send-to-slack": "integration",
  "send-to-sales-channel": "integration",
  "research-company": "integration",
  "find-decision-makers": "integration",
  "claim-communication-attribution": "integration",
  "reassign-communication-sales-attribution": "integration",
  "push-for-approval": "integration",

  // --- admin ---
  "grant-employee-access": "admin",
  "grant-client-access": "admin",
  "consolidate-contact": "admin",
  "consolidate-company": "admin",
  "move-to-internal-projects": "admin",
  "reassign-company": "admin",
  "manage-roles": "admin",
  "reset-password": "admin",
  "toggle-active": "admin",
  toggleActive: "admin",
  export: "admin",
  // Read-only, staff-only forensic view of an entity's change log.
  "view-audit-history": "admin",

  // --- destructive ---
  delete: "destructive",
  deleteEntity: "destructive",
  "delete-recurring-charge": "destructive",
  deleteRecurringCharge: "destructive",
  terminate: "destructive",
  withdraw: "destructive",
};

/**
 * Intra-category ordering (lower = higher in its section). Tunes the sequence
 * WITHIN a category so the most-likely action of each entity leads. Ids absent
 * here default to 0 and keep their authored order via the stable-sort tiebreak.
 */
export const ACTION_ORDER_BY_ID: Partial<Record<ActionId, number>> = {
  // primary — headline next step first; AI assistant last
  "status-dropdown": 0,
  "quick-complete": 0,
  "quick-approve": 0,
  "add-update": 5,
  edit: 20,
  "sync-quickbooks": 30,
  "ask-meridian": 90,

  // communicate
  "send-email": 0,
  call: 1,
  "call-contact": 1,
  "send-text-message": 2,
  "send-text-template": 3,
  "schedule-meeting": 4,
  "log-activity": 5,
  "add-comment": 6,
  "add-task-note": 6,
  "send-ticket-quick-reply": 7,
  "send-ticket-ack-email": 8,
  "send-ticket-resolved-email": 9,

  // create
  "create-tasks": 0,
  "make-ticket": 1,
  "log-deal": 2,
  "log-time-entry": 3,
  "request-quote": 4,
  "generate-contract": 5,
  "create-sm-agreement": 6,
  "create-grant-agreement": 7,
  "convert-to-deal": 8,
  convertToDeal: 8,
  "charge-card-on-file": 9,
  chargeCardOnFile: 9,
  "download-pdf": 10,
  downloadOfferingPdf: 10,
  duplicate: 20,
  "duplicate-task": 20,
  "fill-checklists": 30,
  "fill-both-checklists": 30,
  "fill-client-checklist": 31,
  "fill-telecom-checklist": 32,

  // status — outcomes/assignment first, then date/snooze housekeeping
  "complete-task": 0,
  "mark-won": 0,
  markWon: 0,
  approve: 0,
  "mark-lost": 1,
  markLost: 1,
  reject: 1,
  "close-ticket": 2,
  "mark-paid": 3,
  markDealPaid: 3,
  "mark-in-progress": 4,
  markDealInProgress: 4,
  "assign-deal-to-me": 5,
  "assign-task-to-me": 5,
  "assign-ticket-to-me": 5,
  "assign-ticket": 6,
  "set-due-date": 10,
  "set-followup-date": 11,
  "clear-followup-date": 12,
  "mark-followed-up": 13,
  "unmark-followed-up": 14,
  "snooze-deal": 15,
  snoozeDeal: 15,
  "make-project-channel": 8,
  "unsnooze-deal": 16,
  unsnoozeDeal: 16,
  renew: 20,
  resend: 21,
  "change-schedule": 22,
  changeSchedule: 22,

  // navigate
  "view-details": 0,
  // Directly after View details — the raw agreement is the second thing a rep
  // reaches for on a contract row.
  "view-document": 0,
  "view-company": 1,
  "view-ticket": 1,
  "view-contact": 1,
  viewDeal: 1,
  viewEmployee: 1,
  viewOffering: 1,
  viewCustomer: 1,
  "link-company": 2,
  "link-contact": 3,
  "make-contact": 4,
  "open-slack-channel": 5,
  "copy-link": 6,
  "save-meeting-to-drive": 7,
  "report-issue": 8,
  // Low-prominence triage entry — sits at the tail of the navigate group.
  "view-support-inbox": 9,

  // integration
  "configure-automated-tasks": 0,
  enrich: 1,
  "enrich-contact": 1,
  "enrich-company": 1,
  "research-company": 2,
  "find-decision-makers": 3,
  "send-to-slack": 4,
  "send-to-sales-channel": 5,
  "claim-communication-attribution": 6,
  "reassign-communication-sales-attribution": 7,
  "push-for-approval": 8,

  // admin
  "grant-employee-access": 0,
  "grant-client-access": 1,
  "manage-roles": 2,
  "reset-password": 3,
  "toggle-active": 4,
  toggleActive: 4,
  "consolidate-contact": 5,
  "consolidate-company": 5,
  "reassign-company": 6,
  "move-to-internal-projects": 7,
  export: 9,

  // destructive — terminate/withdraw before the catch-all delete
  terminate: 0,
  withdraw: 1,
  delete: 2,
  deleteEntity: 2,
  "delete-recurring-charge": 2,
  deleteRecurringCharge: 2,
};

/** Categories folded into the collapsed "More…" group. */
export const COLLAPSED_CATEGORIES: ReadonlySet<ActionCategory> = new Set([
  "integration",
  "admin",
]);

/** Label for the collapsed-group trigger. */
export const MORE_ACTIONS_LABEL = "More actions";
